/**
 * Lobby Code Input Node - Player View
 *
 * Host enters configurable lobby details (code, name, password, etc.).
 * Non-host players see a waiting state with animated placeholder.
 * Supports multiple input fields per game requirements.
 */

import type { LobbyCodeField } from "@rivaled/shared";
import { AnimatePresence, motion } from "framer-motion";
import { Key } from "lucide-react";
import { useState } from "react";
import { Button } from "../../../components/ui/Button";
import { cn } from "../../../lib/utils";
import { ErrorBanner } from "../../components/ErrorBanner";
import { LobbyCodeDisplay } from "../../components/LobbyCodeDisplay";
import { LobbyCredentialsCard } from "../../components/LobbyCredentialsCard";
import { NodeCard } from "../../components/NodeCard";
import { NodeHeader } from "../../components/NodeHeader";
import { PlayerAvatar } from "../../components/PlayerAvatar";
import { WaitingDots } from "../../components/WaitingDots";
import { useCurrentPlayerId } from "../../hooks/useCurrentPlayerId";
import type { NodePlayerViewProps } from "../types";

type LobbyCodeInputState = {
  values: Record<string, string>;
  submittedBy: string | null;
};

const DEFAULT_FIELDS: LobbyCodeField[] = [{ key: "lobbyCode", label: "Lobby Code", type: "text" }];

export function LobbyCodeInputView({
  nodeState,
  players,
  onAction,
}: NodePlayerViewProps<LobbyCodeInputState>) {
  const userId = useCurrentPlayerId();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});

  const state = nodeState.state as LobbyCodeInputState;
  const config = nodeState.config as {
    hostUserId?: string;
    instructions?: string;
    fields?: LobbyCodeField[];
    labels?: { title?: string };
  };
  const title = config.labels?.title ?? "Lobby Code";
  const fields = config.fields ?? DEFAULT_FIELDS;
  const hostUserId = config.hostUserId || state.submittedBy;
  const isHost = hostUserId === userId;
  const hostPlayer = players.find((p) => p.userId === hostUserId);
  const hasPassword = fields.some((f) => f.type === "password");
  const isMultiField = fields.length > 1 || hasPassword;

  const allFieldsFilled = fields.every((f) => (fieldValues[f.key] ?? "").trim().length > 0);

  const handleFieldChange = (key: string, value: string) => {
    setFieldValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmitCode = async () => {
    if (!allFieldsFilled) return;

    setIsSubmitting(true);
    setError(null);

    const result = await onAction("submit_code", { values: fieldValues, userId });

    if (!result.success && result.error) {
      setError(result.error);
    }

    setIsSubmitting(false);
  };

  return (
    <NodeCard>
      <NodeHeader
        title={title}
        subtitle={
          state.submittedBy !== null
            ? isMultiField
              ? "Lobby details have been shared"
              : "Use this code to join the game lobby"
            : isHost
              ? "Enter the lobby details from the game"
              : `Waiting for ${hostPlayer?.username ?? "the host"} to share the lobby details`
        }
      />

      <AnimatePresence mode="wait">
        {state.submittedBy !== null ? (
          /* Submitted — display values */
          <motion.div
            key="code-display"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            className="flex flex-col items-center gap-4"
          >
            {!isMultiField ? (
              <LobbyCodeDisplay code={state.values[fields[0].key] ?? ""} />
            ) : (
              <LobbyCredentialsCard
                fields={fields.map((f) => ({
                  key: f.key,
                  label: f.label,
                  value: state.values[f.key] ?? "",
                  type: f.type === "password" ? "password" : "text",
                }))}
              />
            )}
          </motion.div>
        ) : isHost ? (
          /* Host input */
          <motion.div
            key="code-input"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="max-w-sm mx-auto"
          >
            {/* Key icon */}
            <div className="flex justify-center mb-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: 0.15 }}
                className="w-14 h-14 rounded-full bg-brand-500/10 border border-brand-500/20 flex items-center justify-center"
              >
                <Key size={24} className="text-brand-400" aria-hidden="true" />
              </motion.div>
            </div>

            {/* Input fields */}
            <div className="flex flex-col gap-3 mb-3">
              {fields.map((field) => (
                <div key={field.key}>
                  <label className="block text-xs text-surface-400 mb-1.5 font-medium">
                    {field.label}
                  </label>
                  <div className="relative">
                    <div className="absolute -inset-0.5 rounded-lg bg-gradient-to-r from-brand-500/20 via-brand-400/30 to-brand-500/20 blur-sm opacity-60" />
                    <input
                      type={field.type}
                      value={fieldValues[field.key] ?? ""}
                      onChange={(e) => handleFieldChange(field.key, e.target.value)}
                      placeholder={field.placeholder ?? ""}
                      className={cn(
                        "relative w-full px-4 py-3 bg-surface-800 border-2 rounded-lg",
                        "text-base font-mono font-bold text-text-primary",
                        "placeholder:text-surface-500/60",
                        "focus:outline-none focus:border-brand-500/60",
                        "transition-colors duration-200",
                        (fieldValues[field.key] ?? "").trim()
                          ? "border-brand-500/40"
                          : "border-surface-600/60",
                      )}
                      autoComplete="off"
                      spellCheck={false}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Instructions */}
            {config.instructions && (
              <p className="text-xs text-surface-500 text-center mb-3">{config.instructions}</p>
            )}

            <div className="mb-3">
              <ErrorBanner error={error} />
            </div>

            {/* Submit button */}
            <Button
              variant="primary"
              size="lg"
              className="w-full"
              onClick={handleSubmitCode}
              disabled={isSubmitting || !allFieldsFilled}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="inline-block w-4 h-4 border-2 border-surface-900/30 border-t-surface-900 rounded-full animate-spin" />
                  Submitting...
                </span>
              ) : (
                "Submit"
              )}
            </Button>
          </motion.div>
        ) : (
          /* Non-host waiting state */
          <motion.div
            key="waiting"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="flex flex-col items-center gap-5"
          >
            {/* Host avatar */}
            {hostPlayer && (
              <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-surface-800/80 border border-surface-700/50">
                <div className="relative">
                  <PlayerAvatar
                    avatarUrl={hostPlayer.avatarUrl}
                    username={hostPlayer.username}
                    size="lg"
                    ringColor="ring-amber-400/50"
                  />
                  <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-amber-400 border-2 border-surface-900 animate-pulse" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium text-text-primary">{hostPlayer.username}</p>
                  <span className="text-[10px] font-display font-semibold uppercase tracking-wider text-amber-400">
                    Host
                  </span>
                </div>
              </div>
            )}

            {/* Pulsing placeholder */}
            <div className="relative">
              <div className="absolute -inset-1 rounded-lg bg-brand-500/[0.06] blur-md animate-pulse" />
              <div className="relative bg-surface-800 border-2 border-surface-700/50 border-dashed rounded-lg px-8 py-4">
                <div className="flex items-center gap-2">
                  {Array.from({ length: 7 }).map((_, i) => (
                    <motion.div
                      key={i}
                      className={cn("w-4 h-6 rounded-sm bg-surface-700/60", i === 3 && "w-2")}
                      animate={{ opacity: [0.3, 0.6, 0.3] }}
                      transition={{
                        duration: 1.5,
                        repeat: Number.POSITIVE_INFINITY,
                        delay: i * 0.1,
                        ease: "easeInOut",
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Waiting text */}
            <div className="flex items-center gap-3">
              <WaitingDots />
              <span className="text-sm text-text-secondary">Waiting for lobby details</span>
            </div>

            {/* Host disconnected warning */}
            {hostPlayer?.isConnected === false && (
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-error-500/10 border border-error-500/20">
                <div className="w-1.5 h-1.5 rounded-full bg-error-400 animate-pulse" />
                <span className="text-xs text-error-400">
                  Host is offline — waiting for reconnection or timeout
                </span>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </NodeCard>
  );
}
